import {dynamicConfig} from '../init/config';
import {StorageObjectMetadata} from '../models/StorageObjectMetadata';
import {Label} from '../models/Label';
import {ObjectType} from '../models/ObjectType';
import {StorageObject} from '../models/StorageObject';
import {isNullOrEmpty} from './HelperUtils';

export const getEmptyStorageObject = (objectType: ObjectType): StorageObject => {
    let storageObject = {} as StorageObject;
    storageObject.partitionKey = dynamicConfig.DLS_SPACE_NAME;
    storageObject.metadata = getEmptyStorageObjectMetadata();
    storageObject.objectType = objectType;
    storageObject.labels = new Map() as Map<string, Label>;

    return storageObject;
};

export const getEmptyStorageObjectMetadata = (): StorageObjectMetadata => {
    return {} as StorageObjectMetadata;
};

export const getEmptyLabel = (): Label => {
    return {name: '', description: ''} as Label;
};

export const getInitializedLabel = (name: string): Label => {
    if ((name !== undefined) && (!isNullOrEmpty(name))) {
        return {name: name, description: ''} as Label;
    }

    return getEmptyLabel();
};

export const getEmptyLabelEntryMap = (locale: string): Map<string, Label> => {
    let labelMap = new Map() as Map<string, Label>;
    if ((locale === undefined) || (isNullOrEmpty(locale))) {
        return labelMap;
    }

    let mapEntries: [string, Label][] = [];
    mapEntries.push([locale, getEmptyLabel()]);
    labelMap = new Map(mapEntries) as Map<string, Label>;

    return labelMap as Map<string, Label>;
};

export const getLabelMap = (locale: string, labels: Map<string, Label>): Map<string, Label> => {
    if ((labels === undefined) || (!labels)) {
        getEmptyLabelEntryMap(locale);
    }

    return labels;
};

export const getLabelEntryMap = (locale: string, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    return new Map(Object.entries(labelMap)) as Map<string, Label>;
};

export const getLabelEntryMapDefault = (locale: string, labels: Map<string, Label> | null | undefined): Map<string, Label> => {
    if (((labels !== undefined) && labels)
        && ((labels.size > 0) || ((Symbol.iterator in Object.keys(labels)) && (Object.keys(labels).length > 0)))) {
        let labelMap = getLabelEntryMap(locale, labels);
        if ((labelMap.size > 0) || ((Symbol.iterator in Object.keys(labelMap)) && (Object.keys(labelMap).length > 0))) {
            return labelMap;
        } else {
            return labels;
        }
    }

    return getEmptyLabelEntryMap(locale);
};

export const getLabelEntryMapInitialized = (locale: string, name: string | null | undefined, labels: Map<string, Label> | null | undefined): Map<string, Label> => {
    if (((labels !== undefined) && labels)
        && ((labels.size > 0) || ((Symbol.iterator in Object.keys(labels)) && (Object.keys(labels).length > 0)))) {
        return getLabelEntryMapDefault(locale, labels);
    }

    if ((name !== undefined) && name && (!isNullOrEmpty(name.trim()))) {
        let mapEntries: [string, Label][] = [];
        mapEntries.push([locale, getInitializedLabel(name.trim())]);
        let labelMap = new Map(mapEntries) as Map<string, Label>;

        return labelMap as Map<string, Label>;
    }

    return getEmptyLabelEntryMap(locale);
};

export const getLabel = (locale: string, labels: Map<string, Label>): Label => {
    if ((locale === undefined) || (labels === undefined) || (isNullOrEmpty(locale)) || (!labels)) {
        return getEmptyLabel();
    }

    let labelMap = getLabelEntryMap(locale, labels);
    let label = labelMap.get(locale);
    if ((label === undefined) || (!label)) {
        label = getEmptyLabel();
    }

    return label as Label;
};

export const getLabelNative = (locale: string, labels: Map<string, Label>): Label => {
    if ((locale === undefined) || (labels === undefined) || (isNullOrEmpty(locale)) || (!labels)) {
        return getEmptyLabel();
    }

    let labelMap = getLabelMap(locale, labels);
    let label = labelMap.get(locale);
    if ((label === undefined) || (!label)) {
        label = getEmptyLabel();
    }

    return label as Label;
};

export const getLabels = (labels: Map<string, Label>): Label[] => {
    let labelResult: Label[] = [];
    if ((labels === undefined) || (!labels)) {
        return labelResult;
    }

    Object.values(labels).map(value => labelResult.push({name: value.name, description: value.description} as Label));

    return labelResult as Label[];
};

export const getLabelMapKeys = (locale: string, labels: Map<string, Label> | null | undefined): string[] => {
    if ((labels !== undefined) && labels) {
        if (labels.size > 0) {
            return [...Object.keys(Object.fromEntries(labels))];
        } else if ((Symbol.iterator in Object.keys(labels)) && (Object.keys(labels).length > 0)) {
            return [...Object.keys(labels)];
        }
    }

    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        return [...Object.keys(Object.fromEntries(getEmptyLabelEntryMap(locale)))];
    }

    return [];
};

export const getLabelsAsObjectMap = (labels: Map<string, Label> | null | undefined) => {
    let labelMap = new Map();
    if ((labels === undefined) || (!labels)) {
        return labelMap;
    }

    labels.forEach((value, key) =>
        labelMap.set(key,
            {
                name: ((value.name !== undefined) && (!isNullOrEmpty(value.name))) ? value.name.trim() : '',
                description: ((value.description !== undefined) && (!isNullOrEmpty(value.description))) ? value.description.trim() : ''
            } as Label));

    return Object.fromEntries(labelMap);
    // labels.forEach((value, key) => labelMap.set(key, value));
    // return Object.fromEntries(labelMap);
};

export const updateLabelEntryMap = (locale: string, label: Label, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (label !== undefined) && (!isNullOrEmpty(locale)) && label) {
        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
                if (key === locale) {
                    mapEntries.push([key, {name: label.name, description: label.description} as Label]);
                } else {
                    let value = labelMap.get(key);
                    mapEntries.push([key, value ? {
                        name: value.name,
                        description: value.description
                    } as Label : getEmptyLabel()]);
                }
            }
        );

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const setLabelEntryMap = (locales: string[], labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = new Map() as Map<string, Label>;
    if ((locales !== undefined) && (labels !== undefined) && locales && labels) {
        let mapEntries: [string, Label][] = [];
        locales.map(key => {
                if ((key !== undefined) && (!isNullOrEmpty(key))) {
                    if (labels.has(key)) {
                        let value = labels.get(key);
                        mapEntries.push([key, value ? {
                            name: value.name,
                            description: value.description
                        } as Label : getEmptyLabel()]);
                    } else {
                        mapEntries.push([key, getEmptyLabel()]);
                    }
                }
            }
        );

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const spreadLabelEntryMap = (locale: string, label: Label, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
            mapEntries.push([key, {name: label.name, description: label.description} as Label]);
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const addLabelEntryMap = (locale: string, localeToAdd: string, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale)) && (localeToAdd !== undefined) && (!isNullOrEmpty(localeToAdd))) {
        let mapEntries: [string, Label][] = [];
        let keys = Object.keys(Object.fromEntries(labelMap));
        keys.map(key => {
            let value = labelMap.get(key);
            mapEntries.push([key, value ? {
                name: value.name,
                description: value.description
            } as Label : getEmptyLabel()]);
        });

        if (!keys.includes(localeToAdd)) {
            mapEntries.push([localeToAdd, getEmptyLabel()]);
        }

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const addAllLabelEntryMap = (locale: string, localesToAdd: string[], labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale)) && (localesToAdd !== undefined) && localesToAdd && (localesToAdd.length > 0)) {
        let mapEntries: [string, Label][] = [];
        let keys = Object.keys(Object.fromEntries(labelMap));
        keys.map(key => {
            let value = labelMap.get(key);
            mapEntries.push([key, value ? {
                name: value.name,
                description: value.description
            } as Label : getEmptyLabel()]);
        });

        localesToAdd.map(localeToAdd => {
            if (!keys.includes(localeToAdd)) {
                mapEntries.push([localeToAdd, getEmptyLabel()]);
            }
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const deleteLabelEntryMap = (locale: string, labels: Map<string, Label>): Map<string, Label> => {
    let labelMap = getLabelMap(locale, labels);
    if ((locale !== undefined) && (!isNullOrEmpty(locale))) {
        let mapEntries: [string, Label][] = [];
        Object.keys(Object.fromEntries(labelMap)).map(key => {
            if (key !== locale) {
                let value = labelMap.get(key);
                mapEntries.push([key, value ? {
                    name: value.name,
                    description: value.description
                } as Label : getEmptyLabel()]);
            }
        });

        labelMap = new Map(mapEntries) as Map<string, Label>;
    }

    return labelMap as Map<string, Label>;
};

export const validateLabelEntryMap = (labels: Map<string, Label> | null | undefined): boolean => {
    if ((labels === undefined) || (!labels)) {
        return false;
    }

    let result = true;
    labels.forEach((value) => {
        if ((value === undefined) || (!value)
            || (value.name === undefined) || (value.description === undefined)
            || (isNullOrEmpty(value.name)) || (isNullOrEmpty(value.description))) {
            result = result && false;
        }
    });

    return result;
};
